/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { useRef, useCallback } from 'react';

import AtomicSDK, { AACStreamContainer, SDKConfiguration } from '@atomic.io/action-cards-web-sdk';
import { useAtomic } from './AtomicProvider';
import { Box, Stack, styled } from '@mui/material';

const AtomicStyleWrapper = styled(Box)`
  width: 100%;
  display: flex;
  flex-grow: 1;

  .atomic-sdk-frame {
    width: 100% !important;
    height: 100%;
  }
`;

interface AtomicEmbedProps {
  containerId: string;
  children?: React.ReactNode | React.ReactNode[];
}

const AtomicListWrapper: React.FC<AtomicEmbedProps> = ({ containerId, children }) => {
  const { atomicReady, onLinkButtonPressed } = useAtomic();

  // Create a reference to the embedded stream container
  const embedRef = useRef<AACStreamContainer>();
  const elRef = useRef<HTMLDivElement>();

  // Create a callback which we can pass to a ref attribute on any element
  // useCallback here ensures that this code will only run when the ref has changed
  const initAtomicEmbed = useCallback(
    (el: HTMLDivElement) => {
      if (!atomicReady) {
        return;
      }

      if (embedRef.current) {
        // Clean up any existing instance we might have
        embedRef.current.stop();
      }
      if (el && atomicReady) {
        el.innerHTML = '';
        elRef.current = el;

        const config: SDKConfiguration = {
          streamContainerId: containerId,
          enabledUiElements: {
            cardListHeader: false,
            cardListToast: false,
          },

          onSubmitButtonPressed(payload) {
            onLinkButtonPressed(payload);
          },

          onLinkButtonPressed(payload) {
            onLinkButtonPressed(payload);
          },
        };

        // Initiate the atomic stream in the HTML element 'el'
        embedRef.current = AtomicSDK.embed(el, config);
      }
    },
    [atomicReady, containerId],
  );

  React.useEffect(() => {
    if (!atomicReady) {
      return;
    }
    const iframe = document.querySelector('.atomic-sdk-frame') as HTMLIFrameElement;
    if (!iframe) return;

    const handleIframeLoad = () => {
      try {
        const iframeDocument = iframe.contentWindow?.document ?? iframe.contentDocument;
        if (iframeDocument) {
          const styleElement = iframeDocument.createElement('style');
          styleElement.innerHTML = `
          .p13 {
            // padding: 0px;
          }
          `;
          iframeDocument.head.appendChild(styleElement);
        }
      } catch (error) {
        console.error('Error accessing iframe content:', error);
      }
    };

    iframe.addEventListener('load', handleIframeLoad);
    return () => {
      iframe.removeEventListener('load', handleIframeLoad);
    };
  }, [atomicReady, containerId]);

  if (!atomicReady) {
    return <div>Loading...</div>;
  }

  return (
    <Stack gap={1} width='100%' display='flex' flexGrow={1}>
      {children}
      <AtomicStyleWrapper
        id={'atomic-list-' + containerId}
        key={'atomic-list-' + containerId}
        ref={initAtomicEmbed}
        className={`atomic-embed-wrapper-${containerId}`}
      />
    </Stack>
  );
};

export default AtomicListWrapper;
